import { shuffleArray } from 'outpost';
import { CardId, DeckId } from '../project-types.ts';
import { Project } from '../project.ts';
import { BoardCard } from './board-card.ts';
import { BoardPlayerId, BoardPlayer, BoardZone } from './board-types.ts';
import { normalizeString } from '../utils.ts';

export class Board {
    players: { [Key in BoardPlayerId]: BoardPlayer };
    globalCardIndex: { [key: CardId]: BoardCard; } = {};
    deckIds: [string, string] | null = null;

    constructor(project: Project) {
        this.players = {
            p1: initBoardPlayer('p1', project),
            p2: initBoardPlayer('p2', project),
        };
    }

    moveCardToZone(movedCard: BoardCard, targetZone: BoardZone) {
        this.registerCard(movedCard);

        if (movedCard.parentZone) {
            movedCard.parentZone.cards.remove(movedCard);
        }

        if (movedCard.parentCard) {
            movedCard.parentCard.attachedCards.remove(movedCard);
            movedCard.parentCard = null;
        }

        let pushedCards = [];

        if (targetZone.metadata.kind === 'stack') {
            pushedCards.push(...movedCard.attachedCards, movedCard);
            movedCard.attachedCards.forEach(card => card.parentCard = null);
            movedCard.attachedCards = [];
        } else {
            pushedCards.push(movedCard);
        }

        targetZone.cards.push(...pushedCards);
        pushedCards.forEach(card => {
            if (card.parentZone !== targetZone) {
                card.marks = [];
            }
            card.parentZone = targetZone;
        });

        if (targetZone.metadata.autoFlip === 'back') {
            pushedCards.forEach(card => card.isFlipped = true);
        } else if (targetZone.metadata.autoFlip === 'front') {
            pushedCards.forEach(card => card.isFlipped = false);
        }
    }

    attachCard(movedCard: BoardCard, targetCard: BoardCard) {
        this.registerCard(movedCard);

        if (movedCard.parentZone) {
            movedCard.parentZone.cards.remove(movedCard);
            movedCard.parentZone = null;
        }

        if (movedCard.parentCard) {
            movedCard.parentCard.attachedCards.remove(movedCard);
            movedCard.parentCard = null;
        }

        targetCard.attachedCards.push(movedCard);
        targetCard.attachedCards.push(...movedCard.attachedCards);
        movedCard.attachedCards = [];
        targetCard.attachedCards.forEach(child => child.parentCard = targetCard);
    }

    flipCard(card: BoardCard) {
        card.isFlipped = !card.isFlipped;
    }

    revealCard(card: BoardCard) {
        card.isRevealed = !card.isRevealed;
    }

    rotateCard(card: BoardCard) {
        card.isRotated = !card.isRotated;
    }

    markCard(card: BoardCard, playerId: BoardPlayerId) {
        if (card.marks.includes(playerId)) {
            card.marks.remove(playerId);
        } else {
            card.marks.push(playerId);
        }
    }

    shuffleZone(zone: BoardZone | null) {
        if (zone) {
            shuffleArray(zone.cards);
        }
    }

    *cards(): IterableIterator<BoardCard> {
        for (let player of Object.values(this.players)) {
            for (let zone of Object.values(player.zones)) {
                for (let card of zone.cards) {
                    yield card;
                }
            }
        }
    }

    private registerCard(card: BoardCard) {
        this.globalCardIndex[card.cardId] = card;
    }

    getCardById(cardId: CardId): BoardCard | undefined {
        return this.globalCardIndex[cardId];
    }

    initDecks(project: Project, deckIds: [DeckId, DeckId]) {
        this.deckIds = deckIds;

        let decks: { [Key in BoardPlayerId]: DeckId } = {
            p1: deckIds[0],
            p2: deckIds[1]
        };

        let suffix = 1;

        for (let [key, deckId] of Object.entries(decks)) {
            let playerId = key as BoardPlayerId;
            let player = this.players[playerId];
            let deck = project.getDeckById(deckId)!;
            let deckZone = player.zones.find(zone => zone.metadata.isDeck) ?? player.zones[0];

            for (let cardId of deck.cardIds) {
                let card = new BoardCard(`${cardId}#${suffix++}`, playerId);

                this.moveCardToZone(card, deckZone);
            }

            this.shuffleZone(deckZone);
        }
    }
};

function initBoardPlayer(playerId: BoardPlayerId, project: Project): BoardPlayer {
    let zones: BoardZone[] = [];

    for (let list of project.board.rows) {
        for (let metadata of list) {
            let name = normalizeString(metadata.label);
            let zone: BoardZone = {
                name,
                playerId,
                metadata,
                cards: [],
            };

            zones.push(zone);
        }
    }

    return {
        playerId,
        zones: zones as BoardPlayer['zones']
    };
}